/**
 * Shared dot/badge renderers for the project-list colouring feature
 * (SPEC-project-list-colour-rules / R2). Used by BOTH the desktop table
 * (ProjectsPage) and the mobile cards (ProjectMobileCardList) so the two stay
 * in parity.
 *
 * Each renders nothing when its slot has no resolved treatment, keeping the
 * inert-by-default contract: no rules ⇒ no extra DOM.
 */
import { cn } from '@/lib/utils';
import { paletteClass, type ProjectColorTreatment } from '@/lib/projectListColors';

interface ColorIndicatorProps {
  treatment: ProjectColorTreatment;
  /** Optional rule name for the title/aria, surfaced via the row's rule. */
  label?: string;
  className?: string;
}

/**
 * The `dot` slot — a small coloured ● glyph. Renders null when the dot slot is
 * unset so rows without a matching dot rule are visually untouched.
 */
export function ProjectColorDot({ treatment, label, className }: ColorIndicatorProps) {
  const cls = paletteClass(treatment.dot, 'dot');
  if (!cls) return null;
  return (
    <span
      aria-hidden="true"
      title={label}
      data-testid="project-color-dot"
      className={cn('inline-block leading-none', cls, className)}
    >
      ●
    </span>
  );
}

/**
 * The `badge` slot — a small coloured pill. Renders null when the badge slot is
 * unset. Label text is the rule name (falls back to a neutral marker) so the
 * pill carries meaning rather than being a bare swatch.
 */
export function ProjectColorBadge({ treatment, label, className }: ColorIndicatorProps) {
  const cls = paletteClass(treatment.badge, 'badge');
  if (!cls) return null;
  return (
    <span
      data-testid="project-color-badge"
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
        cls,
        className,
      )}
    >
      {label ?? '●'}
    </span>
  );
}
